import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Delete, Edit } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import { Modal, Popconfirm, Table } from "antd";
import { setWorkspaceIdToEdit } from "store/features/userSettings/userSettingsSlice";
import styled from "styled-components";

import { useUser } from "hooks";

import { DELETE_WORKSPACE, WORKSPACES, WorkspaceClientName } from "api/workspace";

import { Workspace, WorkspaceQuery } from "models/workspace";

import { BaseDivider, Heading, SubHeading, Tooltip } from "components/base";

import WorkspaceManagement from "./components/workspace/WorkspaceManagement";

export interface IWorkspaceModal {
  isVisible: boolean;
  mode?: "create" | "edit";
}

export default function WorkspacesList() {
  const dispatch = useDispatch();
  const { user, isReadonly, isAtLeastPowerUser } = useUser();

  const { data: workspacesData, refetch: refetchWorkspaces } = useQuery<WorkspaceQuery>(
    WORKSPACES,
    {
      context: {
        clientName: WorkspaceClientName
      },
      notifyOnNetworkStatusChange: true
    }
  );
  const [deleteWorkspace] = useMutation<WorkspaceQuery>(DELETE_WORKSPACE);

  const [workspaceModal, setWorkspaceModal] = useState<IWorkspaceModal>({
    isVisible: false,
    mode: "create"
  });
  // Take action when a workspace is permanently deleted.
  const handleDeleteWorkspace = useCallback(
    (workspaceId: string) => {
      deleteWorkspace({
        variables: {
          id: workspaceId
        },
        context: {
          clientName: WorkspaceClientName
        },
        onCompleted: () => {
          refetchWorkspaces();
          // Navigate the editing space to the "Explore" workspace after deletion
          // Fall back to the first available workspace if Explore doesn't exist
          const exploreWorkspace =
            workspacesData?.workspaces?.find((ws) => ws.title === "Explore") ??
            workspacesData?.workspaces?.[0];
          dispatch(setWorkspaceIdToEdit(exploreWorkspace?.workspaceId ?? null));
          toast.success("Workspace deleted");
        },
        onError: (error: ApolloError) => {
          // eslint-disable-next-line no-console
          console.error(error.message, error);
          toast.error("Failed to delete workspace");
        }
      });
    },
    [deleteWorkspace, dispatch, refetchWorkspaces, workspacesData?.workspaces]
  );

  const handleFirstConfirmDelete = (workspace: Workspace) => {
    if (workspace.type === "Organization") {
      Modal.confirm({
        title:
          "This will delete the workspace for all users in your organization. Continue?",
        okText: "Delete",
        okType: "danger",
        onOk: () => handleDeleteWorkspace(workspace.workspaceId)
      });
    } else {
      handleDeleteWorkspace(workspace.workspaceId);
    }
  };

  // Take action when "edit workspace" is selected.
  const goToWorkspace = (id: string) => {
    dispatch(setWorkspaceIdToEdit(id));
  };

  //function that displays a table with a list of all workspaces
  const WorkspacesDataTable = () => {
    const columns = [
      {
        title: "Name",
        dataIndex: "title",
        defaultSortOrder: "ascend" as const
      },
      {
        title: "Type",
        dataIndex: "type"
      },
      {
        title: "Owner",
        dataIndex: "ownerName"
      },
      {
        title: "# Dashboards",
        dataIndex: "dashboards",
        render: (_, record: Workspace) => record.dashboards?.length ?? 0
      },
      {
        key: "workspaceId",
        dataIndex: "workspaceId",
        title: "Actions",
        render: (workspaceId, record: Workspace) => (
          <Actions>
            <Tooltip placement="top" title="Edit Workspace">
              <ActionButton
                data-testid={`edit-workspace-${workspaceId}`}
                onClick={() => goToWorkspace(workspaceId)}>
                <Edit fontSize="large" />
              </ActionButton>
            </Tooltip>

            {!(
              record.type === "Personal" &&
              (record.title === "Explore" || record.title === "Midstream")
            ) &&
              (isAtLeastPowerUser || record.owner === user.id) && (
                <Popconfirm
                  title={`Are you sure you want to delete the "${record.title}" workspace?`}
                  okText="Delete"
                  okType="danger"
                  onConfirm={(evt) => {
                    handleFirstConfirmDelete(record);
                    evt.stopPropagation();
                    evt.preventDefault();
                  }}
                  onCancel={(evt) => {
                    evt.stopPropagation();
                    evt.preventDefault();
                  }}>
                  <Tooltip placement="top" title="Delete Workspace">
                    <ActionButton
                      $danger
                      data-testid={`delete-workspace-${record.workspaceId}`}>
                      <Delete fontSize="large" />
                    </ActionButton>
                  </Tooltip>
                </Popconfirm>
              )}
          </Actions>
        )
      }
    ];

    return (
      <>
        <TableContainer>
          <Table
            rowKey="workspaceId"
            columns={columns}
            sortDirections={["ascend", "descend", "ascend"]}
            dataSource={workspacesData?.workspaces}
            pagination={{
              pageSize: 24,
              showSizeChanger: false,
              hideOnSinglePage: true
            }}
            size="middle"
          />
        </TableContainer>
      </>
    );
  };

  return (
    <>
      <RootContainer>
        <HeaderContainer>
          <Heading element="h4">Workspaces</Heading>
          {!isReadonly && (
            <HeaderActionContainer
              onClick={() => setWorkspaceModal({ isVisible: true, mode: "create" })}>
              <AddIcon /> Add Workspace
            </HeaderActionContainer>
          )}
        </HeaderContainer>
        <ContentContainer>
          <BaseDivider />
          <SectionBody>
            <WorkspacesDataTable />

            <SectionHeader>
              <SubHeading>Workspace Details</SubHeading>
              <BaseDivider />
            </SectionHeader>
            <WorkspaceManagement
              workspaceModal={workspaceModal}
              setWorkspaceModal={setWorkspaceModal}
            />
          </SectionBody>
        </ContentContainer>
      </RootContainer>
    </>
  );
}
const SectionHeader = styled.div`
  width: 100%;
  h3 {
    font-size: 1.8rem;
    color: #a2aaad;
    font-weight: var(--fontWeightMedium);
    text-transform: none;
  }
`;
//Styled  Components
export const ActionButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  border: 0;
  margin: 0;
  cursor: pointer;
  background: transparent;
  color: #a2aaad;
  &:hover {
    color: ${(props) => (props.$danger ? "var(--color-danger)" : "var(--color-primary)")};
  }
  &.dashboard-table-spinner[disabled] {
    background: transparent !important;
    box-shadow: none !important;
  }
  &:disabled,
  &[disabled] {
    cursor: pointer;
  }
`;

const HeaderActionContainer = styled.button`
  flex: 1;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  border: 0;
  justify-content: flex-end;
  cursor: pointer;
  background: transparent;
  color: var(--color-primary);
  font-weight: var(--fontWeightMedium);
  &:hover {
    color: var(--color-text);
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 750px;
  max-width: 900px;
  overflow: auto;
`;

const ContentContainer = styled.div`
  min-width: 750px;
  max-width: 900px;
  overflow: auto;
`;

const RootContainer = styled.div`
  gap: 10px;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  text-align: left;
  padding: 32px 100px;
  overflow: auto;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  .ant-divider-horizontal {
    margin: 5px 0;
  }
  .section {
    padding: 33px 0;
  }
  .section-heading {
    font-size: 1.6rem;
    padding-bottom: 22px;
    font-weight: var(--fontWeightMedium);
    padding: 0;
  }
`;

const SectionBody = styled.div`
  padding: 33px 0;
  display: flex;
  flex-direction: column;
  gap: 50px;
`;

export const DashboardRemoveIcon = styled.div`
  font-size: 22px;
  color: inherit;
  line-height: 0;
`;

export const Actions = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: row;
  // gap: 2px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const TableContainer = styled.div`
  .ant-table-row:hover {
    ${Actions} {
      opacity: 1;
    }
  }
  .ant-table-thead > tr > th {
    font-weight: var(--fontWeightBold);
  }
`;
